// WHAT: Page component for managing morning and evening affirmations
// WHY:  Provides interface for viewing and editing affirmations with tips and guidance
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: AffirmationManager component, React Router navigation
// RISKS:
//   - NOTE: Edit mode state is local - may not persist across navigation
//   - NOTE: Navigation path is hardcoded - may break if route changes

import { ArrowLeft, Heart, Sun, Moon, Settings, Sparkles } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Card, Button, Stack, Heading, Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

import { AffirmationManager } from "../components/organisms/AffirmationManager";

/**
 * WHAT:
 *   Page component that provides interface for managing morning and evening
 *   affirmations with edit mode and tips section.
 *
 * WHY:
 *   Enables users to customize their affirmations for morning and evening
 *   sessions with helpful guidance.
 *
 * BEHAVIOR:
 *   - Displays two cards for morning and evening affirmations
 *   - Allows switching to edit mode for each type
 *   - Shows tips for writing and using affirmations
 *   - Provides navigation back to dashboard
 *
 * RISKS:
 *   - NOTE: Edit mode state is local - may not persist across navigation
 *   - NOTE: Navigation path is hardcoded - may break if route changes
 *
 * @returns JSX.Element
 *
 */
export const MiracleFlowAffirmationSettingsTab: React.FC = () => {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState<"evening" | "morning" | "none">("none");

  if (editMode !== "none") {
    return (
      <div className="space-y-6 p-6">
        <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
          <Stack align="center" direction="row" gap={4}>
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
              onClick={() => {
                setEditMode("none");
              }}
              size="sm"
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
            <Stack align="center" direction="row" gap={3}>
              <div
                className={cn(
                  "flex items-center justify-center w-12 h-12 rounded-tracq shadow-tracq-card",
                  editMode === "morning"
                    ? "bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue"
                    : "bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange",
                )}
              >
                <Heart className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
                  {editMode === "morning" ? "Morning" : "Evening"} Affirmation Manager
                </Heading>
                <p className="text-[#94A3B8]">Manage your {editMode} affirmations</p>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        <AffirmationManager
          initialTab={editMode}
          onBack={() => {
            setEditMode("none");
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack align="center" direction="row" justify="between">
          <Stack align="center" direction="row" gap={4}>
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
              onClick={() => {
                const result = navigate("/miracle-flow");
                if (result instanceof Promise) {
                  result.catch(() => {
                    // Navigation errors are handled silently
                  });
                }
              }}
              size="sm"
              variant="outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Stack align="center" direction="row" gap={3}>
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
                  Affirmation Settings
                </Heading>
                <p className="text-[#94A3B8]">Manage your morning and evening affirmations</p>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Card>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Morning Affirmations */}
        <Card
          className="bg-[#1E293B] border-[#334155] hover:border-tracq-bright-blue/50 transition-all duration-200 hover:shadow-tracq-card"
          padding="lg"
          variant="default"
        >
          <Stack direction="col" gap={4}>
            <Stack align="center" direction="row" gap={3}>
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
                <Sun className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
                  Morning Affirmations
                </Heading>
                <p className="text-[#94A3B8] text-sm">Start your day with positive energy</p>
              </Stack>
            </Stack>

            <div className="space-y-3">
              <p className="text-[#CBD5E1] text-sm">
                Morning affirmations help set a positive tone for your day. Choose affirmations that
                inspire and motivate you.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge
                  className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
                  size="sm"
                  variant="info"
                >
                  Positive Energy
                </Badge>
                <Badge
                  className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
                  size="sm"
                  variant="info"
                >
                  Goal Setting
                </Badge>
                <Badge
                  className="bg-tracq-bright-blue/20 text-tracq-bright-blue border-tracq-bright-blue/30"
                  size="sm"
                  variant="info"
                >
                  Self-Confidence
                </Badge>
              </div>
            </div>

            <Button
              className="bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90 text-white font-semibold"
              onClick={() => {
                setEditMode("morning");
              }}
              variant="primary"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Morning Affirmations
            </Button>
          </Stack>
        </Card>

        {/* Evening Affirmations */}
        <Card
          className="bg-[#1E293B] border-[#334155] hover:border-tracq-light-orange/50 transition-all duration-200 hover:shadow-tracq-card"
          padding="lg"
          variant="default"
        >
          <Stack direction="col" gap={4}>
            <Stack align="center" direction="row" gap={3}>
              <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange shadow-tracq-card">
                <Moon className="w-6 h-6 text-white" />
              </div>
              <Stack direction="col" gap={1}>
                <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
                  Evening Affirmations
                </Heading>
                <p className="text-[#94A3B8] text-sm">Reflect and prepare for tomorrow</p>
              </Stack>
            </Stack>

            <div className="space-y-3">
              <p className="text-[#CBD5E1] text-sm">
                Evening affirmations help you reflect on your day and prepare for tomorrow with
                gratitude and intention.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge
                  className="bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30"
                  size="sm"
                  variant="info"
                >
                  Gratitude
                </Badge>
                <Badge
                  className="bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30"
                  size="sm"
                  variant="info"
                >
                  Reflection
                </Badge>
                <Badge
                  className="bg-tracq-light-orange/20 text-tracq-light-orange border-tracq-light-orange/30"
                  size="sm"
                  variant="info"
                >
                  Peace
                </Badge>
              </div>
            </div>

            <Button
              className="bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange hover:from-tracq-deep-orange/90 hover:to-tracq-light-orange/90 text-white font-semibold"
              onClick={() => {
                setEditMode("evening");
              }}
              variant="primary"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Evening Affirmations
            </Button>
          </Stack>
        </Card>
      </div>

      {/* Tips Section */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" gap={3}>
            <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
              Tips for Effective Affirmations
            </Heading>
          </Stack>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3 p-4 bg-[#0F172A] rounded-tracq border border-[#334155]">
              <h4 className="text-[#F1F5F9] font-semibold">Writing Affirmations</h4>
              <ul className="text-[#CBD5E1] text-sm space-y-2">
                <li>• Use present tense (&quot;I am&quot; not &quot;I will be&quot;)</li>
                <li>• Keep them positive and specific</li>
                <li>• Make them personal and meaningful</li>
                <li>• Keep them concise and clear</li>
              </ul>
            </div>
            <div className="space-y-3 p-4 bg-[#0F172A] rounded-tracq border border-[#334155]">
              <h4 className="text-[#F1F5F9] font-semibold">Using Affirmations</h4>
              <ul className="text-[#CBD5E1] text-sm space-y-2">
                <li>• Repeat them daily for best results</li>
                <li>• Say them with conviction and emotion</li>
                <li>• Visualize the outcome as you say them</li>
                <li>• Believe in what you&apos;re affirming</li>
              </ul>
            </div>
          </div>
        </Stack>
      </Card>
    </div>
  );
};
